import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Search, UserPlus, Eye } from "lucide-react";
import ContentLoader from "@/authentication/ContentLoader";
import { db, auth } from "../firebase/config";
import { collection, query, getDocs, setDoc, doc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";

// Define interfaces
interface Affiliation {
  role: string;
  schoolID?: string;
}

interface Student {
  id: string;
  registrationNumber: string;
  firstName: string;
  lastName: string;
  class?: string;
  email?: string;
  phone?: string;
  dormitory?: string | null;
  bedNumber?: string | null;
  photoUrl?: string;
}

interface Dorm {
  id: string;
  name: string;
  location: string;
  capacity: number;
  occupied: number;
  available: number;
  patronTeacherId?: string | null;
  schoolID: string;
  dormUid: string;
  submittedBy: string;
  createdAt: Date;
}

interface StudentsWithoutDormViewProps {
  onBack: () => void;
  dorms: Dorm[];
}

const StudentsWithoutDormView = ({ onBack, dorms }: StudentsWithoutDormViewProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDorm, setSelectedDorm] = useState("");
  const [studentsWithoutDorm, setStudentsWithoutDorm] = useState<Student[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  /* ── Auth & Claims Verification ──────────────────────────────────── */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult(true);
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          if (process.env.NODE_ENV !== "production") {
            console.log("ID Token Result claims:", JSON.stringify(idTokenResult.claims, null, 2));
          }

          let selectedAffiliation: Affiliation | null = null;
          if (defaultAffiliation?.role === "admin" && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            setSchoolID(selectedAffiliation.schoolID);
            setHasValidClaims(true);
          } else {
            // Retry after delay to handle claim propagation
            await new Promise(resolve => setTimeout(resolve, 10000));
            await user.getIdToken(true);
            const retryIdTokenResult = await user.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            selectedAffiliation = retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID
              ? retryDefaultAff
              : retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID) || null;

            if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && retrySchoolID === selectedAffiliation.schoolID) {
              setSchoolID(selectedAffiliation.schoolID);
              setHasValidClaims(true);
            } else {
              throw new Error("Invalid admin claims after retry");
            }
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          toast({
            title: "Authentication Error",
            description: "Failed to verify permissions. Please log in again.",
            variant: "destructive",
            duration: 5000,
          });
          navigate("/login-page");
        }
      } else {
        setSchoolID(null);
        setHasValidClaims(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  /* ── Fetch Students Without Dorm ───────────────────────────────── */
  const fetchStudentsWithoutDorm = useCallback(async () => {
    if (!hasValidClaims || !schoolID) {
      setIsLoadingStudents(false);
      return;
    }

    setIsLoadingStudents(true);
    try {
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const q = query(studentsRef); // Fetch all students to ensure we catch those with missing/null/empty dormitory fields
      const snapshot = await getDocs(q);
      const students: Student[] = snapshot.docs.map(doc => {
        const data = doc.data();
        // Use className if available, otherwise construct from grade and class
        let displayClass = data.className || "";
        if (!displayClass && data.grade && data.class) {
          displayClass = `${data.grade} ${data.class}`;
        } else if (!displayClass && data.grade) {
          displayClass = data.grade;
        } else if (!displayClass && data.class) {
          displayClass = data.class;
        }

        return {
          id: doc.id,
          registrationNumber: data.registrationNumber || "",
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          class: displayClass,
          email: data.email || undefined,
          phone: data.phoneNumber || undefined,
          dormitory: data.dormitory || null,
          bedNumber: data.bedNumber || null,
          photoUrl: data.photoUrl || undefined,
        };
      }).filter(student => !student.dormitory);
      setStudentsWithoutDorm(students);
      if (process.env.NODE_ENV !== "production") {
        console.log("Fetched Students Without Dorm:", students);
      }
    } catch (error: any) {
      console.error("Error fetching students:", error);
      toast({
        title: "Error Loading Students",
        description: `Failed to load students: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsLoadingStudents(false);
    }
  }, [hasValidClaims, schoolID, toast]);

  useEffect(() => {
    if (!isAuthLoading && hasValidClaims && schoolID) {
      fetchStudentsWithoutDorm();
    } else {
      setIsLoadingStudents(false);
    }
  }, [isAuthLoading, hasValidClaims, schoolID, fetchStudentsWithoutDorm]);

  /* ── Filter Students ────────────────────────────────────── */
  const filteredStudents = studentsWithoutDorm.filter(student =>
    student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableDorms = dorms.filter(dorm => dorm.available > 0);

  /* ── Assign Dorm Handler ─────────────────────────────────── */
  const handleAssignDorm = async (studentId: string) => {
    if (!selectedDorm) {
      toast({
        title: "No Dormitory Selected",
        description: "Please select a dormitory to assign the student.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    if (!schoolID) {
      toast({
        title: "Authentication Error",
        description: "School ID missing. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    const selectedDormData = dorms.find(dorm => dorm.dormUid === selectedDorm);
    if (!selectedDormData) {
      toast({
        title: "Invalid Dormitory",
        description: "Selected dormitory not found.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    if (selectedDormData.available <= 0) {
      toast({
        title: "Dormitory Full",
        description: `The dormitory ${selectedDormData.name} has no available capacity.`,
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      // Update student document
      const studentRef = doc(db, `schools/${schoolID}/students`, studentId);
      await setDoc(studentRef, {
        dormitory: selectedDormData.name,
        bedNumber: `B${String(selectedDormData.occupied + 1).padStart(3, '0')}`,
      }, { merge: true });

      // Update dormitory document
      const dormRef = doc(db, `schools/${schoolID}/dorms`, selectedDormData.dormUid);
      await setDoc(dormRef, {
        occupied: selectedDormData.occupied + 1,
        available: selectedDormData.available - 1,
      }, { merge: true });

      // Update local state to remove assigned student
      setStudentsWithoutDorm(prev => prev.filter(student => student.id !== studentId));

      toast({
        title: "Student Assigned",
        description: `Student has been assigned to ${selectedDormData.name}.`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error assigning dorm:", error);
      toast({
        title: "Assignment Failed",
        description: `Failed to assign student: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  /* ── Handle View Student (Placeholder) ───────────────────── */
  const handleViewStudent = (studentId: string) => {
    navigate(`/students/${studentId}`); // Placeholder: Navigate to student details page
  };

  /* ── Loading UI handled by ContentLoader ─────────────────────────────── */

  /* ── Unauthorized UI ───────────────────────────────────── */
  if (!isAuthLoading && !hasValidClaims) {
    return (
      <div className="flex h-full w-full justify-center items-center p-6">
        <Card className="w-full max-w-md border-none bg-white dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl text-gray-900 dark:text-white">
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-400">
              You do not have the necessary permissions to view or assign students. Please contact support or re-login.
            </p>
            <Button
              onClick={() => navigate("/login-page")}
              className="mt-4 bg-blue-600 text-white hover:bg-blue-700"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ── Main UI ───────────────────────────────────────────── */
  return (
    <ContentLoader isLoading={isAuthLoading || (hasValidClaims && isLoadingStudents)} message={isLoadingStudents ? "Loading students..." : "Loading authentication..."}>
      <div className="w-full h-full">
        <div className="mb-6">
        <Button
          onClick={onBack}
          variant="outline"
          className="mb-4 flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dormitories
        </Button>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Students Without Dormitory
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Assign dormitories to students who need accommodation
            </p>
            <Badge
              variant="outline"
              className="mt-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
            >
              {filteredStudents.length} Students Need Assignment
            </Badge>
          </div>
        </div>

        {/* Search and Selection Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
            />
          </div>
          <Select value={selectedDorm} onValueChange={setSelectedDorm}>
            <SelectTrigger className="w-full sm:w-48 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
              <SelectValue placeholder="Select Dormitory" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
              {availableDorms.length === 0 ? (
                <SelectItem value="none" disabled className="text-gray-500 dark:text-gray-400">
                  No available dormitories
                </SelectItem>
              ) : (
                availableDorms.map((dorm) => (
                  <SelectItem
                    key={dorm.dormUid}
                    value={dorm.dormUid}
                    className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {dorm.name} ({dorm.available} available)
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Students Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStudents.map((student) => (
          <Card
            key={student.id}
            className="border-none bg-white dark:bg-gray-800 hover:shadow-lg transition-shadow"
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/50 rounded-full flex items-center justify-center overflow-hidden border border-orange-200">
                    {student.photoUrl ? (
                      <img src={student.photoUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-orange-600 dark:text-orange-400 font-semibold">
                        {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {student.firstName} {student.lastName}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {student.registrationNumber}
                    </p>
                  </div>
                </div>
                <Badge
                  variant="destructive"
                  className="bg-red-600 text-white"
                >
                  No Dorm
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Class:</span>
                  <p className="text-gray-900 dark:text-white">{student.class || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Email:</span>
                  <p className="text-gray-900 dark:text-white">{student.email || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Phone:</span>
                  <p className="text-gray-900 dark:text-white">{student.phone || "N/A"}</p>
                </div>
              </div>

              <div className="flex justify-between space-x-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleViewStudent(student.id)}
                  className="flex items-center gap-1 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                >
                  <Eye className="h-3 w-3" />
                  View
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleAssignDorm(student.id)}
                  disabled={!selectedDorm || availableDorms.length === 0}
                  className="bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-800 dark:hover:bg-blue-900"
                >
                  <UserPlus className="h-3 w-3 mr-1" />
                  Assign
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredStudents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm
              ? "No students found matching your search."
              : "No students found without dormitory assignment."}
          </p>
        </div>
      )}
      </div>
    </ContentLoader>
  );
};

export default StudentsWithoutDormView;